import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { LightningBolt } from "@/components/LightningBolt";

const KINDS = [
  { key: "demo", label: "Demo" },
  { key: "pilot", label: "Pilot" },
  { key: "investor", label: "Investor" },
  { key: "halo", label: "Halo reservations" },
];

export default function AdminLeads() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [active, setActive] = useState("demo");
  const [data, setData] = useState({ demo: [], pilot: [], investor: [], halo: [] });
  const [summary, setSummary] = useState({ demo: 0, pilot: 0, investor: 0, halo: 0, total: 0, haloPaid: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [demo, pilot, investor, halo, sum, haloSummary] = await Promise.all([
        api.get("/admin/leads/demo"),
        api.get("/admin/leads/pilot"),
        api.get("/admin/leads/investor"),
        api.get("/admin/halo/reservations"),
        api.get("/admin/leads/summary"),
        api.get("/admin/halo/summary"),
      ]);
      setData({ demo: demo.data, pilot: pilot.data, investor: investor.data, halo: halo.data });
      setSummary({
        ...sum.data,
        halo: haloSummary.data.total,
        haloPaid: haloSummary.data.paid,
        total: sum.data.total + haloSummary.data.total,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <>
      <SEO title="Leads · LightningFlow Admin" description="Internal leads dashboard." />
      <div className="min-h-screen bg-white text-ink-900" data-testid="admin-leads-page">
        {/* App-shell header */}
        <header className="border-b border-ink-100 bg-white sticky top-0 z-40">
          <div className="lf-container flex h-20 items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="admin-leads-home-link">
              <LightningBolt size={53} />
              <span className="font-display text-base font-extrabold">LightningFlow</span>
              <span className="hidden sm:inline-flex font-mono text-[10px] uppercase tracking-[0.18em] text-ink-500 border border-ink-100 px-1.5 py-0.5 ml-1">
                Console
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <span className="hidden md:inline-flex font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">
                {user?.email || "admin"}
              </span>
              <button
                type="button"
                className="lf-btn-ghost"
                onClick={load}
                disabled={loading}
                data-testid="admin-leads-refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                type="button"
                className="lf-btn-secondary"
                onClick={handleLogout}
                data-testid="admin-leads-logout"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>
        </header>

        <section className="lf-container py-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="lf-eyebrow">Leads</div>
              <h1 className="mt-3 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
                Incoming pipeline.
              </h1>
              <p className="mt-3 text-ink-500 max-w-xl">
                Submitted from public marketing forms. Live data only — empty
                states are shown explicitly when no submissions exist.
              </p>
            </div>
            <div className="grid grid-cols-5 gap-px bg-ink-100 border border-ink-100">
              <SummaryCell label="Demo" value={summary.demo} />
              <SummaryCell label="Pilot" value={summary.pilot} />
              <SummaryCell label="Investor" value={summary.investor} />
              <SummaryCell label="Halo" value={summary.haloPaid ? `${summary.haloPaid}/${summary.halo}` : summary.halo} />
              <SummaryCell label="Total" value={summary.total} accent />
            </div>
          </div>

          <Tabs value={active} onValueChange={setActive} data-testid="admin-leads-tabs">
            <TabsList className="bg-ink-50 border border-ink-100 rounded-none h-auto p-1">
              {KINDS.map((k) => (
                <TabsTrigger
                  key={k.key}
                  value={k.key}
                  className="rounded-none data-[state=active]:bg-white data-[state=active]:text-ink-900 data-[state=active]:shadow-none border-r border-ink-100 last:border-r-0 font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2.5"
                  data-testid={`admin-leads-tab-${k.key}`}
                >
                  {k.label}
                  <span className="ml-2 font-mono text-[10px] text-ink-400">
                    {k.key === "halo" && summary.haloPaid ? `${summary.haloPaid}/${summary.halo}` : summary[k.key]}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
            {KINDS.map((k) => (
              <TabsContent key={k.key} value={k.key} className="mt-6">
            <LeadsTable kind={k.key} rows={data[k.key]} loading={loading} />
              </TabsContent>
            ))}
          </Tabs>
        </section>
      </div>
    </>
  );
}

function SummaryCell({ label, value, accent }) {
  return (
    <div
      className={`bg-white px-5 py-3 min-w-[100px] ${accent ? "bg-ink-900 text-white" : ""}`}
      data-testid={`admin-leads-summary-${label.toLowerCase()}`}
    >
      <div className={`font-mono text-[10px] uppercase tracking-[0.18em] ${accent ? "text-ink-300" : "text-ink-400"}`}>
        {label}
      </div>
      <div className="font-display text-2xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function LeadsTable({ kind, rows, loading }) {
  if (loading && rows.length === 0) {
    return (
      <div className="border border-ink-100 bg-white p-12 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-ink-400" data-testid={`admin-leads-loading-${kind}`}>
        Loading…
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div
        className="border border-ink-100 bg-white p-12 flex flex-col items-center justify-center text-center"
        data-testid={`admin-leads-empty-${kind}`}
      >
        <Inbox className="h-8 w-8 text-ink-300 mb-4" strokeWidth={1.5} />
        <div className="font-display text-xl font-bold">No {kind} requests yet.</div>
        <p className="text-sm text-ink-500 mt-2 max-w-md">
          When prospects submit the public form, entries will appear here. We
          do not seed mock data.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-ink-100 bg-white overflow-x-auto" data-testid={`admin-leads-table-${kind}`}>
      <Table>
        <TableHeader>
          <TableRow className="bg-ink-50 border-b border-ink-100">
            <TableHead className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">When</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">Name</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">Email</TableHead>
          <TableHead className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">
              {kind === "investor" ? "Firm" : kind === "halo" ? "Product" : "Company"}
            </TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">{kind === "halo" ? "Status" : "Role"}</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">Notes</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const p = r.payload || {};
            const orgKey = kind === "investor" ? "firm" : kind === "halo" ? "product_interest" : "company";
            return (
              <TableRow key={r.id} className="border-b border-ink-100 hover:bg-ink-50/50">
                <TableCell className="font-mono text-xs text-ink-500 whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString()}
                </TableCell>
                <TableCell className="text-sm font-medium">{p.name}</TableCell>
                <TableCell className="text-sm">
                  <a
                    href={`mailto:${p.email}`}
                    className="underline-offset-2 hover:underline"
                    data-testid={`admin-lead-email-${r.id}`}
                  >
                    {p.email}
                  </a>
                </TableCell>
                <TableCell className="text-sm">{p[orgKey] || "—"}</TableCell>
                <TableCell className="text-sm">{kind === "halo" ? r.status : p.role || "—"}</TableCell>
                <TableCell className="text-xs text-ink-600 max-w-[280px] truncate">
                  {kind === "halo" ? `${p.country || "—"} · ${p.use_case || "—"} · ${p.price_band || "—"}` : p.message || p.timeline || p.environment_size || p.team_size || p.stage_focus || "—"}
                </TableCell>
                <TableCell>
                  <a
                    href={`mailto:${p.email}?subject=${encodeURIComponent("LightningFlow — " + kind + " follow-up")}`}
                    className="lf-btn-ghost px-2 h-8 text-xs"
                    data-testid={`admin-lead-reply-${r.id}`}
                  >
                    <Mail className="h-3.5 w-3.5" /> Reply
                  </a>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
